import { useTranslation } from 'react-i18next';

import { Box, Button, Grid, Modal, Paper, SvgIcon, Typography } from '@mui/material';

import { SnakebarToaster } from '@/components';
import { DatePickerField, RadioGroupField } from '@/components/Form';
import { CloseIcon } from '@/utils/iconSVG';

import {
  stylesFormalitiesModal,
  stylesFormalitiesModalTitle,
  stylesFormalitiesModalClose,
  stylesFormalitiesModalFooter,
  stylesFormalitiesModalHeader,
} from './CreateNewRequestModal.styles';
import { useCreateNewRequest, useCreateNewRequestModal } from './hooks';

type AddCustomsDocumentsModalProps = {
  isOpen: boolean;
  onCloseCreateNewRequestModal?: () => void;
};

export const CreateNewRequestModal: React.FunctionComponent<AddCustomsDocumentsModalProps> = ({
  isOpen,
  onCloseCreateNewRequestModal,
}) => {
  const { t } = useTranslation('customsPass_servicesOffer');

  const {
    directionRadioGroupsOptions,
    newRequestForm,
    handleOnCloseModal,
    handleChangeDirection,
    handleChangeCrossingRequestDate,
  } = useCreateNewRequestModal({ onCloseCreateNewRequestModal });

  const {
    handleCloseToaster,
    handleCreateNewFolder,
    isCreateNewRequestInError,
    isCreateFolderByTypologyPending,
    isUpdateCrossingRequestedDatePending,
  } = useCreateNewRequest(newRequestForm);
  return (
    <Modal
      open={isOpen}
      onClose={handleOnCloseModal}
      data-testid='add-formalities-modal'
      aria-labelledby='add-formalities-modal-title'
      aria-describedby='add-formalities-modal-description'
    >
      <Paper sx={stylesFormalitiesModal}>
        <Box sx={stylesFormalitiesModalHeader}>
          <Typography sx={stylesFormalitiesModalTitle} variant='bodyXLBold' component='div'>
            {t('customsPass.servicesOffer.modal.title')}
          </Typography>
          <Button sx={stylesFormalitiesModalClose} color='alternative' onClick={onCloseCreateNewRequestModal}>
            <SvgIcon component={CloseIcon} />
          </Button>
        </Box>
        <Grid container spacing={3} pr={3}>
          <Grid item xs={12}>
            <Box>{t('customsPass.servicesOffer.modal.text1')}</Box>
          </Grid>
          <Grid item xs={12}>
            <Box>
              <RadioGroupField
                id='direction'
                value={newRequestForm.direction}
                options={directionRadioGroupsOptions}
                label={t('customsPass.servicesOffer.modal.crossing.direction.label')}
                onChange={handleChangeDirection}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box>
              <DatePickerField
                id='crossing-estimatedDate-label'
                label={t('customsPass.servicesOffer.modal.crossing.estimatedDate.label')}
                onChange={handleChangeCrossingRequestDate}
                value={newRequestForm.crossingRequestedDate}
                min={new Date().toISOString().split('T')[0]}
              />
            </Box>
          </Grid>
          {/* <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox onChange={handleCheckRenewOrder} />}
              label={t('customsPass.servicesOffer.modal.checkbox.renewRequest.label')}
            />

            <Typography>{t('customsPass.servicesOffer.modal.checkbox.renewRequest.content')}</Typography>
          </Grid> */}
          <Grid item xs={12}>
            <Box sx={stylesFormalitiesModalFooter}>
              <Button variant='contained' color='secondaryGrey' onClick={onCloseCreateNewRequestModal}>
                {t('customsPass.servicesOffer.modal.cancelButton.label')}
              </Button>

              <Button
                variant='contained'
                onClick={handleCreateNewFolder}
                disabled={
                  !newRequestForm.direction || isCreateFolderByTypologyPending || isUpdateCrossingRequestedDatePending
                }
              >
                {t('customsPass.servicesOffer.modal.createButton.label')}
              </Button>
            </Box>
          </Grid>
        </Grid>
        {isCreateNewRequestInError && (
          <SnakebarToaster
            severity='error'
            content={t('customsPass.servicesOffer.modal.createNewRequest.error.toasterMessage')}
            onClose={handleCloseToaster}
            isOpen
          />
        )}
      </Paper>
    </Modal>
  );
};
